import { FormProvider } from '@ariakit/react'

import { gql } from '../../../../../__generated__/graphql/catalog'
import { ProductCatalogVariantGroup } from '../../../../../__generated__/graphql/catalog/graphql'
import { VariantSelectionFitSelect } from '../../../../../app/_components/forms/VariantSelectionFitField'
import {
  VariantSelectionForm,
  useVariantSelectionForm,
} from '../../../../../app/_components/forms/VariantSelectionForm'
import { VariantSelectionSizeSelect } from '../../../../../app/_components/forms/VariantSelectionSizeField'
import { VariantSelectionFormPurchasingSubmit } from '../../../../../app/_components/forms/VariantSelectionSubmit'
import { NextLink } from '../../../../../app/_components/navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariant,
} from '../../../../../app/_components/pricing/PricingRange.client'
import { useFetchNostoPlacementData } from '../../../../../app/_hooks/useFetchNostoPlacementData'
import { useNextQuery } from '../../../../../app/_hooks/useNextQuery'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'

import * as Styles from './CompleteTheSet.styles'

export type CompleteTheSetProps = {
  product: {
    handle: string
  }
  variantGroup: {
    color: Pick<NonNullable<ProductCatalogVariantGroup['color']>, 'id'> | undefined | null
    primaryCategory: Pick<ProductCatalogVariantGroup['primaryCategory'], 'key'>
  }
}

export const CompleteTheSet = ({ product, variantGroup }: CompleteTheSetProps) => {
  const nostoPlacementId = 'productpage-nosto-1'
  const {
    data: { skusForVariantGroups, title },
  } = useFetchNostoPlacementData(nostoPlacementId)

  const { data, previousData, status } = useNextQuery(
    gql(`
      query GetCompleteTheSetAssociation($handle: String!, $variantGroupColor: String!) {
        productCatalogStandardProduct(handle: $handle) {
          variantGroup(color: $variantGroupColor) {
            __typename
            id
            associations(first: 1, type: COMPLETE_THE_SET) {
              variantGroup {
                __typename
                id
                associatedExclusiveCollectionHandle
                variant {
                  sku
                }
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        handle: product.handle,
        variantGroupColor: variantGroup.color?.id ?? '',
      },
    }
  )

  const dataToUse = data ?? previousData
  const initiallyPending = status === 'idle' || (status === 'pending' && !dataToUse)
  const defaultSku =
    dataToUse?.productCatalogStandardProduct.variantGroup.associations?.[0]?.variantGroup.variant
      ?.sku
  const categoryIsValid =
    variantGroup.primaryCategory.key === 'SCRUB_TOP' ||
    variantGroup.primaryCategory.key === 'SCRUB_PANTS'

  // Nosto can do fallback campaigns that are unrelated to complete the set. It also doesn't translate titles. And merch can alter campaigns without us knowing.
  // So, despite title comparisons being brittle, checking for 'complete' is the best option I see right now to prevent this from breaking as often as it does.
  const fallbackSku =
    categoryIsValid && !initiallyPending && title?.toLowerCase().includes('complete')
      ? skusForVariantGroups?.[0]
      : undefined
  const sku = defaultSku ?? fallbackSku

  if (!sku) return null

  return (
    <FormProvider>
      <CompleteTheSetDetails
        sku={sku}
        associatedExclusiveCollectionHandle={
          dataToUse?.productCatalogStandardProduct.variantGroup.associations?.[0]?.variantGroup
            .associatedExclusiveCollectionHandle
        }
        key={sku}
      />
    </FormProvider>
  )
}

const CompleteTheSetDetails = ({
  sku,
  associatedExclusiveCollectionHandle,
}: {
  sku: string
  associatedExclusiveCollectionHandle: string | undefined | null
}) => {
  const { formStore, queryVariables } = useVariantSelectionForm({
    defaultValues: {
      initialColor: '',
      initialFit: '',
      initialSize: '',
    },
  })

  const {
    data: currentData,
    previousData,
    status,
  } = useNextQuery(
    gql(`
      query GetCompleteTheSetVariation(
          $collectionHandle: String, 
          $sku: String!, 
          $colorFieldValue: String!, 
          $fitFieldValue: String!, 
          $sizeFieldValue: String!
      ) {
        productCatalogVariant(sku: $sku) {
          __typename
          id
          group {
            __typename
            id
            ...VariantSelectionFormGroupFields
            urlPath
            images(first: 1, fit: $fitFieldValue) {
              url
            }
            product {
              __typename
              id
              ...VariantSelectionFormProductFields
            }
          }
        }
      }
    `),
    {
      variables: {
        ...queryVariables,
        sku: sku,
        collectionHandle: associatedExclusiveCollectionHandle,
      },
    }
  )

  const data = currentData ?? previousData

  const { selectAndAddVariant: strings, completeTheSet } = useTranslation()

  if (!data || data.productCatalogVariant.group.product.__typename === 'ProductCatalogKit')
    return null

  const product = data.productCatalogVariant.group.product
  const variantGroup = data.productCatalogVariant.group
  const selectedVariant = data.productCatalogVariant.group.variant
  const imageUrl = variantGroup.images?.[0]?.url
  const pricing = selectedVariant ? derivePricingForVariant(selectedVariant) : undefined

  return (
    <Styles.CompleteTheSetWrap>
      <Styles.Title asTag='h3'>{completeTheSet.headline}</Styles.Title>
      <Styles.Details>
        {imageUrl && (
          <NextLink href={variantGroup.urlPath}>
            <Styles.Thumbnail
              alt={variantGroup.title}
              loading='lazy'
              src={imageUrl}
              widths={{
                unit: 'px',
                sm: 150,
                md: 150,
              }}
              aspectRatios={{
                sm: 1,
                md: 1,
              }}
            ></Styles.Thumbnail>
          </NextLink>
        )}
        <Styles.DetailsText>
          <Styles.ProductName>{variantGroup.title}</Styles.ProductName>
          {variantGroup.color?.displayValue ? (
            <Styles.ColorText>{variantGroup.color.displayValue}</Styles.ColorText>
          ) : null}
        </Styles.DetailsText>
      </Styles.Details>
      <VariantSelectionForm
        store={formStore}
        product={product}
        selectedVariant={selectedVariant}
        variantGroup={variantGroup}
        trackingCategory={'complete the set'}
        loading={status === 'pending'}
      >
        <Styles.SelectWrap>
          <VariantSelectionSizeSelect />
          <VariantSelectionFitSelect />
        </Styles.SelectWrap>

        <VariantSelectionFormPurchasingSubmit
          isPrimaryCtaForPageView={false}
          variant='secondary'
          children={
            pricing ? (
              <>
                <PricingRange {...pricing} />
                <span>{' • ' + strings.addToBagButton.cta}</span>
              </>
            ) : null
          }
        ></VariantSelectionFormPurchasingSubmit>
      </VariantSelectionForm>
    </Styles.CompleteTheSetWrap>
  )
}
